<template>
  <div class="global-container">
    <div class="content">
      <!-- Header Section - Employees -->
      <div v-if="activeTab === 'Employees'" class="header-section">
        <div class="title-section">
          <h1 class="page-title">Salaries</h1>
          <p class="page-description">Manage employee salaries and payments</p>
        </div>
        <router-link to="/salaries/employees/new" class="add-button">
          <span class="plus-icon">+</span>
          Add Employee
        </router-link>
      </div>

      <!-- Header Section - Payments -->
      <div v-else-if="activeTab === 'Payments'" class="header-section">
        <div class="title-section">
          <h1 class="page-title">Salary Payments</h1>
          <p class="page-description">Track and manage salary payments</p>
        </div>
        <div class="header-buttons">
          <button class="btn-outline" @click="showRecordExpenseModal = true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="3" x2="9" y2="21"></line>
            </svg>
            Record Salary Expense
          </button>
          <button class="add-button" @click="showAddPaymentModal = true">
            <span class="plus-icon">+</span>
            Add Payment
          </button>
        </div>
      </div>

      <!-- Header Section - Reports -->
      <div v-else class="header-section">
        <div class="title-section">
          <h1 class="page-title">Salary Reports</h1>
          <p class="page-description">View salary reports and analytics</p>
        </div>
      </div>

      <!-- Stat Cards - Employees -->
      <div v-if="activeTab === 'Employees'" class="stats-grid stats-3">
        <div class="stat-card">
          <div class="stat-icon employees-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Total Employees</p>
            <p class="stat-value">{{ stats.totalEmployees }}</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon payroll-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Monthly Payroll</p>
            <p class="stat-value">{{ formatCurrency(stats.monthlyPayroll) }}</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon departments-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Departments</p>
            <p class="stat-value">{{ stats.totalDepartments }}</p>
          </div>
        </div>
      </div>

      <!-- Stat Cards - Payments -->
      <div v-else-if="activeTab === 'Payments'" class="stats-grid stats-2">
        <div class="stat-card">
          <div class="stat-icon pending-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Pending Payments</p>
            <p class="stat-value">{{ formatCurrency(pendingTotal) }}</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon paid-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Total Paid</p>
            <p class="stat-value">{{ formatCurrency(paidTotal) }}</p>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-container">
        <div class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab"
            :class="['tab-button', { active: activeTab === tab }]"
            @click="activeTab = tab"
          >
            {{ tab }}
          </button>
        </div>
      </div>

      <!-- Employees Tab -->
      <div v-show="activeTab === 'Employees'" class="tab-content">
        <div class="search-container">
          <div class="search-input-wrapper">
            <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input v-model="searchQuery" type="text" placeholder="Search employees..." class="search-input" @input="handleSearch" />
          </div>
        </div>
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading employees...</p>
        </div>
        <div v-else-if="!isMobileView" class="table-section">
          <h3 class="table-title">All Employees</h3>
          <div class="table-container">
            <table class="employees-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="employees.employees.length === 0">
                  <td colspan="6" class="empty-state">
                    <p>No employees found. Click "Add Employee" to create one.</p>
                  </td>
                </tr>
                <tr v-for="employee in employees.employees" :key="employee.id || employee.partyId">
                  <td><span class="employee-name">{{ employee.fullName || employee.name }}</span></td>
                  <td>{{ employee.department }}</td>
                  <td>{{ employee.designation || employee.position }}</td>
                  <td>
                    <span class="salary">{{ formatCurrency(employee.basicSalary || employee.salary) }}</span>
                    <span class="salary-period">/month</span>
                  </td>
                  <td>
                    <span :class="['status-badge', (employee.status || 'active').toLowerCase()]">
                      {{ employee.status || 'Active' }}
                    </span>
                  </td>
                  <td>
                    <div class="action-buttons">
                      <button class="action-btn view-btn" title="View" @click="viewEmployee(employee)">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      <button class="action-btn edit-btn" title="Edit" @click="editEmployee(employee)">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button class="action-btn delete-btn" title="Delete" @click="confirmDelete(employee)">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="mobile-cards">
          <h3 class="table-title">All Employees</h3>
          <div v-if="employees.employees.length === 0" class="empty-state">
            <p>No employees found. Click "Add Employee" to create one.</p>
          </div>
          <div v-for="employee in employees.employees" :key="employee.id || employee.partyId" class="employee-card">
            <div class="card-header">
              <div class="card-header-left">
                <span class="employee-name">{{ employee.fullName || employee.name }}</span>
                <span :class="['status-badge', (employee.status || 'active').toLowerCase()]">{{ employee.status || 'Active' }}</span>
              </div>
              <div class="card-actions">
                <button class="action-btn view-btn" title="View" @click="viewEmployee(employee)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                </button>
                <button class="action-btn edit-btn" title="Edit" @click="editEmployee(employee)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                </button>
                <button class="action-btn delete-btn" title="Delete" @click="confirmDelete(employee)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div class="card-row"><span class="card-label">Department</span><span class="card-value">{{ employee.department }}</span></div>
              <div class="card-row"><span class="card-label">Position</span><span class="card-value">{{ employee.designation || employee.position }}</span></div>
              <div class="card-row"><span class="card-label">Salary</span><span class="card-value salary">{{ formatCurrency(employee.basicSalary || employee.salary) }}<span class="salary-period">/month</span></span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payments Tab -->
      <SalaryPaymentsTab
        v-show="activeTab === 'Payments'"
        :selectedMonth="selectedMonth"
        :selectedYear="selectedYear"
        :payments="salaryPayments"
        :loading="salaryLoading"
        @update:selectedMonth="selectedMonth = $event"
        @update:selectedYear="selectedYear = $event"
      />

      <!-- Reports Tab -->
      <div v-show="activeTab === 'Reports'" class="tab-content">
        <div class="empty-state-box">
          <p>Reports coming soon...</p>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <RecordSalaryExpenseModal
      :isOpen="showRecordExpenseModal"
      :monthLabel="currentMonthLabel"
      :month="selectedMonth"
      :year="selectedYear"
      @close="showRecordExpenseModal = false"
      @recorded="loadSalaryPayments"
    />
    <AddSalaryPaymentModal
      :isOpen="showAddPaymentModal"
      :monthLabel="currentMonthLabel"
      :month="selectedMonth"
      :year="selectedYear"
      :salaryPayments="salaryPayments"
      @close="showAddPaymentModal = false"
      @paid="loadSalaryPayments"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="confirm-modal">
        <h3>Delete Employee</h3>
        <p>Are you sure you want to delete "{{ employeeToDelete?.fullName || employeeToDelete?.name }}"?</p>
        <div class="confirm-actions">
          <button class="btn-cancel" @click="cancelDelete">Cancel</button>
          <button class="btn-delete" @click="handleDelete" :disabled="loading">
            {{ loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import SalaryPaymentsTab from '@/components/SalaryPaymentsTab.vue'
import RecordSalaryExpenseModal from '@/components/RecordSalaryExpenseModal.vue'
import AddSalaryPaymentModal from '@/components/AddSalaryPaymentModal.vue'

export default {
  name: 'Salaries',
  components: {
    SalaryPaymentsTab,
    RecordSalaryExpenseModal,
    AddSalaryPaymentModal
  },
  data() {
    const now = new Date()
    return {
      activeTab: 'Employees',
      searchQuery: '',
      tabs: ['Employees', 'Payments', 'Reports'],
      windowWidth: window.innerWidth,
      searchTimeout: null,
      showDeleteConfirm: false,
      employeeToDelete: null,
      // Payments tab (1-based month for API)
      selectedMonth: now.getMonth() + 1,
      selectedYear: now.getFullYear(),
      // Modals
      showRecordExpenseModal: false,
      showAddPaymentModal: false
    }
  },
  computed: {
    ...mapGetters('employee', ['employees', 'stats', 'loading']),
    ...mapGetters('salary', { salaryPayments: 'payments', salaryLoading: 'loading' }),
    isMobileView() {
      return this.windowWidth < 768
    },
    months() {
      return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    },
    currentMonthLabel() {
      return `${this.months[this.selectedMonth - 1]} ${this.selectedYear}`
    },
    pendingTotal() {
      return this.salaryPayments
        .filter(p => (p.status || '').toLowerCase() === 'pending')
        .reduce((sum, p) => sum + (p.netSalary || (p.grossSalary - (p.totalDeductions || 0))), 0)
    },
    paidTotal() {
      return this.salaryPayments
        .filter(p => (p.status || '').toLowerCase() === 'paid')
        .reduce((sum, p) => sum + (p.netSalary || (p.grossSalary - (p.totalDeductions || 0))), 0)
    }
  },
  watch: {
    activeTab(tab) {
      if (tab === 'Payments') {
        this.loadSalaryPayments()
      }
    },
    selectedMonth() {
      if (this.activeTab === 'Payments') {
        this.loadSalaryPayments()
      }
    },
    selectedYear() {
      if (this.activeTab === 'Payments') {
        this.loadSalaryPayments()
      }
    }
  },
  methods: {
    ...mapActions('employee', ['fetchEmployees', 'fetchStats', 'deleteEmployee']),
    ...mapActions('salary', ['fetchSalaryPayments']),

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0)
    },

    async loadData() {
      const params = {}
      if (this.searchQuery) params.search = this.searchQuery
      await Promise.all([this.fetchEmployees(params), this.fetchStats()])
    },

    async loadSalaryPayments() {
      await this.fetchSalaryPayments({ month: this.selectedMonth, year: this.selectedYear })
    },

    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => { this.loadData() }, 300)
    },

    viewEmployee(employee) {
      console.log('View employee:', employee)
    },

    editEmployee(employee) {
      const partyId = employee.partyID || employee.id
      this.$router.push(`/salaries/employees/${partyId}/edit`)
    },

    confirmDelete(employee) {
      this.employeeToDelete = employee
      this.showDeleteConfirm = true
    },

    cancelDelete() {
      this.employeeToDelete = null
      this.showDeleteConfirm = false
    },

    async handleDelete() {
      if (!this.employeeToDelete) return
      try {
        const partyId = this.employeeToDelete.partyID || this.employeeToDelete.id
        await this.deleteEmployee(partyId)
        this.cancelDelete()
      } catch (error) {
        console.error('Delete failed:', error)
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    clearTimeout(this.searchTimeout)
  }
}
</script>

<style scoped>
/* Header Section */
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 48px;
}

.title-section { flex: 1; }

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

.header-buttons {
  display: flex;
  gap: 12px;
  align-items: center;
}

.add-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
  white-space: nowrap;
  text-decoration: none;
}

.add-button:hover { background: #1d4ed8; }

.btn-outline {
  background: white;
  color: #1f2937;
  border: 1px solid #d1d5db;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-outline:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.plus-icon { font-size: 18px; }

/* Stat Cards */
.stats-grid {
  display: grid;
  gap: 24px;
  margin-bottom: 48px;
}

.stats-3 { grid-template-columns: repeat(3, 1fr); }
.stats-2 { grid-template-columns: repeat(2, 1fr); }

.stat-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.employees-icon { background: #dbeafe; color: #2563eb; }
.payroll-icon { background: #dcfce7; color: #16a34a; }
.departments-icon { background: #ede9fe; color: #7c3aed; }
.pending-icon { background: #fef3c7; color: #d97706; }
.paid-icon { background: #dcfce7; color: #16a34a; }

.stat-content { flex: 1; }

.stat-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

/* Tabs */
.tabs-container { margin-bottom: 32px; }

.tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
}

.tab-button {
  background: none;
  border: none;
  padding: 16px 0;
  margin-right: 32px;
  font-size: 15px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.2s ease;
}

.tab-button:hover { color: #1f2937; }

.tab-button.active {
  color: #2563eb;
  border-bottom: 3px solid #2563eb;
  margin-bottom: -2px;
}

/* Tab Content */
.tab-content { animation: fadeIn 0.2s ease; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loading-state p { margin-top: 16px; color: #6b7280; }

/* Empty State */
.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

.empty-state-box {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
}

/* Search */
.search-container { margin-bottom: 32px; }

.search-input-wrapper {
  position: relative;
  width: 100%;
  max-width: 350px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-input::placeholder { color: #9ca3af; }

/* Table */
.table-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
}

.table-container { overflow-x: auto; }

.employees-table { width: 100%; border-collapse: collapse; }
.employees-table thead tr { border-bottom: 1px solid #e5e7eb; }

.employees-table th {
  padding: 12px 0;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.employees-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.employees-table tbody tr:hover { background-color: #f9fafb; }

.employees-table td {
  padding: 16px 0;
  font-size: 14px;
  color: #1f2937;
}

.employee-name { font-weight: 600; color: #1f2937; }
.salary { font-weight: 600; color: #1f2937; }
.salary-period { font-size: 12px; color: #9ca3af; margin-left: 4px; }

/* Status Badge */
.status-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.active { background: #dbeafe; color: #1e40af; }
.status-badge.inactive { background: #f3f4f6; color: #6b7280; }

/* Action Buttons */
.action-buttons { display: flex; gap: 8px; }

.action-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #6b7280;
}

.action-btn:hover { border-color: #d1d5db; background: #f9fafb; }
.view-btn:hover { color: #2563eb; }
.edit-btn:hover { color: #16a34a; }
.delete-btn:hover { color: #dc2626; }

/* Mobile Cards */
.mobile-cards {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.mobile-cards .table-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
}

.employee-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.employee-card:last-child { margin-bottom: 0; }

.employee-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.employee-card .card-header-left { display: flex; align-items: center; gap: 12px; }
.employee-card .card-actions { display: flex; gap: 8px; }
.employee-card .card-body { padding: 16px; }

.employee-card .card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.employee-card .card-row:last-child { border-bottom: none; }
.employee-card .card-label { font-size: 13px; color: #6b7280; font-weight: 500; }
.employee-card .card-value { font-size: 14px; color: #1f2937; }
.employee-card .card-value.salary { font-weight: 600; }

/* Delete Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.confirm-modal h3 { font-size: 18px; font-weight: 700; color: #1f2937; margin-bottom: 12px; }
.confirm-modal p { color: #6b7280; margin-bottom: 24px; }
.confirm-actions { display: flex; gap: 12px; justify-content: flex-end; }

.btn-cancel {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel:hover { background: #f3f4f6; }

.btn-delete {
  padding: 10px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-delete:hover { background: #dc2626; }
.btn-delete:disabled { opacity: 0.7; cursor: not-allowed; }

/* Responsive */
@media (max-width: 768px) {
  .header-section { flex-direction: column; gap: 24px; }
  .page-title { font-size: 28px; }
  .stats-grid, .stats-2, .stats-3 { grid-template-columns: 1fr; }
  .tab-button { margin-right: 16px; }
  .mobile-cards { padding: 16px; }
  .header-buttons { flex-direction: column; width: 100%; }
}
</style>
